import {PepRelationship} from '@sollapay/enums';
import {FieldMultiSelect, FormSectionHeading, Tabs, TabsContent} from '@sollapay/ui/components';
import {useState} from 'react';
import {useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {EMPTY_PEP_PARTY, isPepPartyComplete} from './pep-form.utils';
import {PepPartyFields} from './PepPartyFields';

import type {PepPartyFormValues} from './pep-form.utils';
import type {TabItem} from '@sollapay/ui/components';
import type {FieldValues, Control, UseFormRegister} from 'react-hook-form';

interface PepSectionProps<T extends FieldValues> {
  control: Control<T>;
  register: UseFormRegister<T>;
  isCorporate: boolean;
  pepPath?: string;
  fe: (key: string) => string | undefined;
  /** When false, native RHF required rules in party fields are omitted. Defaults to true. */
  enforceRequired?: boolean;
  /** Parties field array, shared with the sibling `PepToggleField` via `usePepParties` — do not call `useFieldArray` again here. */
  fields: (PepPartyFormValues & {id: string})[];
  append: (value: PepPartyFormValues, options?: {shouldFocus?: boolean}) => void;
  remove: (index?: number | number[]) => void;
}

const RELATIONSHIP_OPTIONS: PepRelationship[] = [
  PepRelationship.SIGNATORY,
  PepRelationship.SPOUSE,
  PepRelationship.FAMILY_MEMBER,
  PepRelationship.BUSINESS_ASSOCIATE,
  PepRelationship.CEO
];

export function PepSection<T extends FieldValues>({
  control,
  register,
  isCorporate,
  pepPath = 'pep',
  fe,
  enforceRequired = true,
  fields,
  append,
  remove
}: PepSectionProps<T>) {
  const {t} = useTranslation('trustAccounts');
  const p = `activation.packages.trustDetails.form.fields.pep` as const;
  const op = `activation.packages.trustDetails.form.options` as const;

  const [activeTab, setActiveTab] = useState<string>('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = control as unknown as Control<any>;

  const isPep = useWatch({control: c, name: `${pepPath}.isPep`});

  if (!isPep) return null;

  const selectedRelationships = fields
    .map(f => f.relationship)
    .filter((r): r is PepRelationship => !!r);

  const handleRelationshipsChange = (values: string[]) => {
    const next = values as PepRelationship[];
    const added = next.filter(r => !selectedRelationships.includes(r));
    const toRemove = selectedRelationships.filter(r => !next.includes(r));
    const indicesToRemove = toRemove
      .map(rel => fields.findIndex(f => f.relationship === rel))
      .filter((idx): idx is number => idx !== -1);
    if (indicesToRemove.length) remove(indicesToRemove);
    added.forEach(rel => {
      append({...EMPTY_PEP_PARTY, relationship: rel}, {shouldFocus: false});
    });
    if (added.length > 0) {
      setActiveTab(added[added.length - 1] ?? '');
    }
  };

  const relationshipOptions = RELATIONSHIP_OPTIONS.map(rel => ({
    value: rel,
    label: t(`${op}.pepRelationships.${rel}`)
  }));

  const pepFields = fields
    .map((f, i) => ({field: f, idx: i}))
    .filter(({field}) => !!field.relationship);

  const tabs: TabItem[] = pepFields.map(({field}) => ({
    value: field.relationship as string,
    label: t(`${op}.pepRelationships.${field.relationship}`),
    icon: isPepPartyComplete(field, {isCorporate}) ? 'check-circle' : 'alert-circle',
    iconClassName: isPepPartyComplete(field, {isCorporate}) ? undefined : 'text-destructive'
  }));

  const resolvedActiveTab =
    activeTab && tabs.find(tab => tab.value === activeTab) ? activeTab : (tabs[0]?.value ?? '');

  const resolvedDescriptionKey = isCorporate
    ? 'sectionDescription'
    : 'sectionDescriptionIndividual';

  return (
    <div className="flex flex-col gap-4">
      <FormSectionHeading
        title={t(`${p}.sectionTitle`)}
        description={t(`${p}.${resolvedDescriptionKey}`)}
      />

      {isCorporate && (
        <FieldMultiSelect
          label={t(`${p}.relationshipsLabel`)}
          placeholder={t(`${p}.relationshipsPlaceholder`)}
          options={relationshipOptions}
          value={selectedRelationships}
          onValueChange={handleRelationshipsChange}
          error={fe(`${pepPath}.parties`)}
        />
      )}

      {isCorporate && tabs.length > 0 && (
        <Tabs
          tabs={tabs}
          value={resolvedActiveTab}
          onValueChange={setActiveTab}
          variant="secondary"
        >
          {pepFields.map(({field, idx}) => (
            <TabsContent key={field.relationship as string} value={field.relationship as string}>
              <PepPartyFields
                partyPath={`${pepPath}.parties.${idx}`}
                isCorporate
                control={control}
                register={register}
                fe={fe}
                enforceRequired={enforceRequired}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}

      {!isCorporate && fields.length > 0 && (
        <PepPartyFields
          partyPath={`${pepPath}.parties.0`}
          isCorporate={false}
          control={control}
          register={register}
          fe={fe}
          enforceRequired={enforceRequired}
        />
      )}
    </div>
  );
}
